import { Link } from 'react-router-dom';
import { Post } from '@/mocks/posts';
import { getPostPublishDate, formatPublishDate } from '@/utils/scheduler';
import { categories } from '@/mocks/categories';

interface PostCardProps {
  post: Post;
  variant?: 'default' | 'featured' | 'compact';
}

export default function PostCard({ post, variant = 'default' }: PostCardProps) {
  const publishDate = getPostPublishDate(post);
  const category = categories.find((c) => c.slug === post.category);
  const imageAlt = `${post.title} — ${category?.name ?? 'Relationship Advice'} | roameurope.blog`;

  if (variant === 'featured') {
    return (
      <Link to={`/post/${post.slug}`} className="group block cursor-pointer">
        <article className="relative overflow-hidden rounded-lg h-[480px] md:h-[560px]">
          <img
            src={post.image}
            alt={imageAlt}
            title={post.title}
            fetchPriority="high"
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            {category && (
              <span
                className="inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-3"
                style={{ backgroundColor: `${category.color}22`, color: category.color, border: `1px solid ${category.color}44` }}
              >
                {category.name}
              </span>
            )}
            <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight mb-3 group-hover:text-rose-200 transition-colors">
              {post.title}
            </h2>
            <p className="text-stone-300 text-sm md:text-base leading-relaxed mb-4 hidden sm:block line-clamp-2">
              {post.excerpt}
            </p>
            <div className="flex items-center gap-4 text-stone-400 text-xs">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {post.author}
              </span>
              <time dateTime={publishDate.toISOString()} className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {formatPublishDate(publishDate)}
              </time>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" />
                {post.readTime} min read
              </span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link to={`/post/${post.slug}`} className="group flex gap-4 cursor-pointer">
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={post.image}
            alt={imageAlt}
            title={post.title}
            loading="lazy"
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          {category && (
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: category.color }}>
              {category.name}
            </span>
          )}
          <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100 leading-snug mt-0.5 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors line-clamp-2">
            {post.title}
          </h3>
          <time dateTime={publishDate.toISOString()} className="text-xs text-stone-400 dark:text-stone-500 mt-1 block">{formatPublishDate(publishDate)}</time>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/post/${post.slug}`} className="group block cursor-pointer">
      <article className="bg-white dark:bg-stone-800 rounded-lg overflow-hidden border border-stone-100 dark:border-stone-700 hover:border-rose-100 dark:hover:border-rose-900 transition-all duration-300">
        <div className="h-52 overflow-hidden">
          <img
            src={post.image}
            alt={imageAlt}
            title={post.title}
            loading="lazy"
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          {category && (
            <span
              className="inline-block text-xs font-semibold uppercase tracking-widest px-2.5 py-0.5 rounded-full mb-3"
              style={{ backgroundColor: `${category.color}15`, color: category.color }}
            >
              {category.name}
            </span>
          )}
          <h3 className="text-base font-bold text-stone-800 dark:text-stone-100 leading-snug mb-2 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors line-clamp-2">
            {post.title}
          </h3>
          <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed line-clamp-2 mb-4">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-stone-400 dark:text-stone-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {post.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {post.readTime} min
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
